import React, { useEffect, useState } from 'react';
import { useT } from '../i18n';
import type { BonusView } from '../lib/bonus-state';
import { BONUS_AVAILABLE_IN_BUILD } from '../lib/bonus-availability';

/**
 * CF-BONUS-WOW-V1 — the ENTRY moment: a one-time full-screen welcome takeover for the
 * first-deposit offer (PL #12717: the inline banner alone "completely blends in", and
 * there was no bonus moment "when people enter").
 *
 * Eligibility gate is IDENTICAL to BonusTeaser/BonusPromoBanner: null unless the build
 * participates AND the server confirmed `none` + `offer_available`. Nothing client-side
 * ever decides eligibility.
 *
 * Display FREQUENCY (not eligibility) is client-side: localStorage
 * `cf_bonus_welcome_seen_v1`, following the shipped `cf_first_open_seen` idiom
 * (App.tsx — best-effort flag, NOT identity). This is PER-CLIENT display-frequency
 * state only — never a durable per-player guarantee (Scorer 0432 §2). Marked seen on
 * explicit dismiss, backdrop tap, primary CTA tap, and terms-link tap; never merely on
 * render. If storage is unavailable (private mode) the in-memory `dismissed` state
 * still prevents re-showing within the mounted session, and nothing crashes. After the
 * one takeover, the persistent gold banner carries the offer.
 *
 * Copy is the approved `bonus.teaser.*` set plus the host-chosen shipped CTA key —
 * no new strings. Dismiss is an × button + backdrop tap: no copy needed.
 * Sits at z-[90], below DepositModal's z-[100], so CTA → deposit stacks cleanly.
 * The ONLY motion is the one entrance transition, disabled under
 * `prefers-reduced-motion` via `motion-reduce:transition-none`; the panel glow is a
 * STATIC boxShadow — nothing loops (Scorer 0439 finding 1).
 */

const SEEN_KEY = 'cf_bonus_welcome_seen_v1';

function readSeen(): boolean {
    try { return localStorage.getItem(SEEN_KEY) !== null; } catch { return false; }
}
function writeSeen(): void {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* best-effort only */ }
}

export interface BonusWelcomeProps {
    view: BonusView;
    /** Already-translated CTA label — the HOST owns action-honesty (connect vs deposit copy). */
    primaryLabel: string;
    /** Connect flow when disconnected, deposit modal when connected. Supplied by the host. */
    onPrimary: () => void;
    termsHref?: string;
}

export function BonusWelcome({ view, primaryLabel, onPrimary, termsHref }: BonusWelcomeProps) {
    const t = useT();
    // Frequency state resolved once at mount; entered drives the entrance transition.
    const [dismissed, setDismissed] = useState<boolean>(() => readSeen());
    const [entered, setEntered] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(id);
    }, []);

    if (!BONUS_AVAILABLE_IN_BUILD) return null;
    if (view.kind !== 'none' || !view.offerAvailable) return null;
    if (dismissed) return null;

    const close = () => { writeSeen(); setDismissed(true); };
    const claim = () => { writeSeen(); setDismissed(true); onPrimary(); };

    return (
        <div
            className={`fixed inset-0 z-[90] flex items-center justify-center px-5 transition-opacity duration-300 motion-reduce:transition-none ${entered ? 'opacity-100' : 'opacity-0'}`}
            style={{ background: 'rgba(0,0,0,0.86)' }}
            onClick={close}
        >
            <div
                className={`not-prose relative w-full max-w-sm rounded-3xl p-6 text-center transition-transform duration-300 motion-reduce:transition-none ${entered ? 'scale-100' : 'scale-95'}`}
                style={{
                    background: 'linear-gradient(160deg, #f7dd7f 0%, #e8c24f 55%, #d4af37 100%)',
                    // STATIC glow — deliberately not the looping .glow-gold class (Scorer 0439:
                    // the only motion on this surface is the one entrance transition, which
                    // motion-reduce disables; nothing loops).
                    boxShadow: '0 0 60px rgba(212,175,55,0.45)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={close}
                    aria-label={t('common.close_btn')}
                    className="absolute top-3 right-4 text-[20px] font-bold leading-none"
                    style={{ color: 'rgba(26,20,8,0.55)' }}
                >
                    ×
                </button>
                {/* Decorative only — not copy, no i18n key, no semantic meaning (Scorer 0432 emoji ruling). */}
                <div aria-hidden="true" className="text-[52px] leading-none mb-2">🎁</div>
                <p className="text-[24px] font-extrabold leading-tight px-3" style={{ color: '#1a1408' }}>
                    {t('bonus.teaser.headline')}
                </p>
                <p className="text-[14px] mt-3" style={{ color: '#3a2f10' }}>
                    {t('bonus.teaser.body')}
                </p>
                <button
                    onClick={claim}
                    className="w-full py-3.5 rounded-2xl text-[16px] font-extrabold mt-5"
                    style={{ background: '#14100a', color: 'var(--gold)' }}
                >
                    {primaryLabel}
                </button>
                <p className="text-[11px] mt-3" style={{ color: '#4a3d16' }}>
                    {t('bonus.teaser.availability')}
                </p>
                {termsHref && (
                    <a
                        href={termsHref}
                        onClick={close}
                        style={{ color: '#241c06', fontSize: 12, textDecoration: 'underline', display: 'inline-block', marginTop: 6 }}
                    >
                        {t('bonus.teaser.terms_link')} →
                    </a>
                )}
            </div>
        </div>
    );
}
